/**
 * Tiny platform-detection helpers. Capacitor exposes a global Capacitor
 * object on native; we read it without statically importing so this
 * file works on plain web builds too.
 */

type CapacitorGlobal = {
  isNativePlatform?: () => boolean;
  Plugins?: Record<string, unknown>;
};

function cap(): CapacitorGlobal | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
}

export function isNative(): boolean {
  try {
    return !!cap()?.isNativePlatform?.();
  } catch {
    return false;
  }
}

/**
 * On-device OCR is native-only — ML Kit doesn't run in a web view.
 * Web builds hide the receipt scan button entirely.
 */
export function supportsOCR(): boolean {
  return isNative();
}

/** Camera works on native via the plugin, on web via getUserMedia, and
 *  on essentially every modern browser via input[type=file][capture]. */
export function supportsCamera(): boolean {
  if (isNative()) return true;
  if (typeof navigator !== 'undefined' && navigator.mediaDevices) return true;
  if (typeof document !== 'undefined') {
    // input[type=file] is universally available; the `capture` hint is
    // ignored on desktop but lets mobile browsers default to the rear
    // camera, which is what we want for receipts.
    return true;
  }
  return false;
}

/**
 * Whether the receipt-attach button should be shown. True on every
 * platform that can produce an image — native via the Camera plugin,
 * web via the file-input fallback. OCR availability is separate
 * (`supportsOCR`); the scanner sheet runs OCR when it can and falls
 * through to a manual-entry review when it can't.
 */
export function supportsReceiptCapture(): boolean {
  return supportsCamera();
}

/** Look up a Capacitor plugin by name without static import. */
export function getCapacitorPlugin<T = unknown>(name: string): T | null {
  const plugins = cap()?.Plugins;
  if (!plugins) return null;
  return (plugins[name] as T | undefined) ?? null;
}
